import { Style, Icon, Stroke, Circle, Fill, Text } from "ol/style";
import Point from "ol/geom/Point";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import WKT from "ol/format/WKT";
import { isEmpty, buffer, getHeight } from "ol/extent";
import { looksLikeMortonCode, mortonWktFromCode } from "../util/mortonTile";

async function fetchBoundaryWKT(boundaryCode, idToken) {
  const url = `${process.env.BOUNDARY_GEOM_URL}?country=${encodeURIComponent(boundaryCode)}`;
  const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new Error(`Boundary code not found: ${boundaryCode}`);
  }
  const wkt = await response.text();
  if (!wkt || wkt.trim().length === 0) {
    throw new Error(`Boundary code returned empty result: ${boundaryCode}`);
  }
  return wkt;
}

/**
 * Parse mixed input containing WKT strings and boundary codes
 * @param {string} input - Mixed input with WKT geometries and boundary codes
 * @returns {Object} - { wktStrings: string[], boundaryCodes: string[], mortonCodes: string[], valid: boolean, error: string }
 */
function parseInputTokens(input) {
  const result = {
    wktStrings: [],
    boundaryCodes: [],
    mortonCodes: [],
    valid: true,
    error: null,
  };

  if (!input || input.trim().length === 0) {
    return result;
  }

  // Check for balanced parentheses first
  let balance = 0;
  for (let char of input) {
    if (char === "(") balance++;
    if (char === ")") balance--;
    if (balance < 0) {
      result.valid = false;
      result.error =
        "Unbalanced parentheses: closing parenthesis without opening";
      return result;
    }
  }
  if (balance !== 0) {
    result.valid = false;
    result.error = "Unbalanced parentheses: unclosed parenthesis";
    return result;
  }

  // Find all parenthesis groups with their preceding text
  const segments = [];
  let i = 0;

  while (i < input.length) {
    // Skip whitespace
    while (i < input.length && /\s/.test(input[i])) {
      i++;
    }

    if (i >= input.length) break;

    // Check if we're at the start of a potential WKT geometry
    const remainingText = input.substring(i);

    // Look for WKT type keywords (POINT, LINESTRING, POLYGON, MULTIPOINT, MULTILINESTRING, MULTIPOLYGON, GEOMETRYCOLLECTION)
    // with optional Z, M, or ZM modifiers
    const wktPattern =
      /^(POINT|LINESTRING|POLYGON|MULTIPOINT|MULTILINESTRING|MULTIPOLYGON|GEOMETRYCOLLECTION)(\s+Z|\s+M|\s+ZM)?\s*\(/i;
    const match = remainingText.match(wktPattern);

    if (match) {
      // This is a WKT geometry - find the matching closing parenthesis
      const startIdx = i;
      let parenDepth = 0;
      let foundStart = false;
      let endIdx = i;

      for (let j = i; j < input.length; j++) {
        if (input[j] === "(") {
          parenDepth++;
          foundStart = true;
        } else if (input[j] === ")") {
          parenDepth--;
          if (foundStart && parenDepth === 0) {
            endIdx = j + 1;
            break;
          }
        }
      }

      const wktString = input.substring(startIdx, endIdx).trim();
      if (wktString) {
        segments.push({ type: "wkt", value: wktString });
      }
      i = endIdx;
    } else {
      // This is not a WKT geometry - treat as potential boundary code
      // Extract until next WKT geometry or end of string
      let nextWktStart = input.length;

      // Find the next WKT geometry start
      for (let j = i + 1; j < input.length; j++) {
        const testText = input.substring(j);
        if (wktPattern.test(testText)) {
          nextWktStart = j;
          break;
        }
      }

      const tokenEnd = nextWktStart;
      const token = input.substring(i, tokenEnd).trim();

      if (token) {
        // Split by whitespace into individual codes; canonical Morton tile
        // codes are resolved to polygons, anything else stays a boundary code.
        const codes = token.split(/\s+/).filter((c) => c.length > 0);
        codes.forEach((code) => {
          if (looksLikeMortonCode(code)) {
            segments.push({ type: "morton", value: code });
          } else {
            segments.push({ type: "boundary", value: code });
          }
        });
      }
      i = tokenEnd;
    }
  }

  // Separate WKT strings, Morton tile codes and boundary codes
  segments.forEach((seg) => {
    if (seg.type === "wkt") {
      result.wktStrings.push(seg.value);
    } else if (seg.type === "morton") {
      result.mortonCodes.push(seg.value);
    } else {
      result.boundaryCodes.push(seg.value);
    }
  });

  return result;
}

/**
 * Convert WKT string to OpenLayers features
 * @param {string} wktString - WKT geometry string
 * @param {number} index - Feature index for labeling
 * @param {string} [boundaryCode] - Optional boundary code for label suffix
 * @returns {Feature} - OpenLayers feature
 */
function wktStringToFeature(wktString, index, boundaryCode) {
  const format = new WKT();
  const feature = format.readFeature(wktString, {
    dataProjection: "EPSG:4326",
    featureProjection: "EPSG:3857",
  });
  const suffix = boundaryCode ? `: ${boundaryCode}` : "";
  feature.set("label", `${index}${suffix}`);
  return feature;
}

/**
 * Process mixed input (WKT strings and boundary codes) into features
 * @param {string} input - Mixed input string
 * @param {string} idToken - Authentication token for boundary service
 * @returns {Promise<Array>} - Array of OpenLayers features
 */
async function parseInputToFeatures(input, idToken) {
  const parsed = parseInputTokens(input);

  if (!parsed.valid) {
    throw new Error(parsed.error);
  }

  const features = [];
  let featureIndex = 0;

  // Process WKT strings
  for (const wktString of parsed.wktStrings) {
    try {
      const feature = wktStringToFeature(wktString, featureIndex);
      features.push(feature);
      featureIndex++;
    } catch (err) {
      throw new Error(
        `Invalid WKT geometry: ${wktString.substring(0, 50)}... - ${err.message}`,
        { cause: err },
      );
    }
  }

  // Process Morton tile codes into tile polygons
  for (const code of parsed.mortonCodes) {
    try {
      const feature = wktStringToFeature(
        mortonWktFromCode(code),
        featureIndex,
        code,
      );
      feature.set("mortonCode", code);
      features.push(feature);
      featureIndex++;
    } catch (err) {
      throw new Error(`Invalid Morton tile code '${code}': ${err.message}`, {
        cause: err,
      });
    }
  }

  // Process boundary codes
  for (const boundaryCode of parsed.boundaryCodes) {
    try {
      const wkt = await fetchBoundaryWKT(boundaryCode, idToken);

      // The boundary service might return multiple WKT strings
      const boundaryParsed = parseInputTokens(wkt);

      for (const wktString of boundaryParsed.wktStrings) {
        const feature = wktStringToFeature(
          wktString,
          featureIndex,
          boundaryCode,
        );
        feature.set("boundaryCode", boundaryCode);
        features.push(feature);
        featureIndex++;
      }
    } catch (err) {
      throw new Error(
        `Failed to fetch boundary code '${boundaryCode}': ${err.message}`,
        { cause: err },
      );
    }
  }

  return features;
}

export function getWKTLayer(wktInputControl, view, azureLoginControl) {
  const vectorSource = new VectorSource({});

  const styleFunction = function (feature) {
    const geometry = feature.getGeometry();
    const LINE_COLOR = "red";
    const FILL_COLOR = "white";
    const styles = [
      // linestring
      new Style({
        stroke: new Stroke({
          color: LINE_COLOR,
          width: 10,
        }),
      }),
      new Style({
        stroke: new Stroke({
          color: FILL_COLOR,
          width: 8,
        }),
      }),
      new Style({
        image: new Circle({
          fill: new Fill({
            color: FILL_COLOR,
          }),
          stroke: new Stroke({
            color: LINE_COLOR,
            width: 1,
          }),
          radius: 10,
        }),
      }),
    ];
    if (feature.get("label") != undefined) {
      styles.push(
        new Style({
          text: new Text({
            text: feature.get("label"),
            fill: new Fill({
              color: LINE_COLOR,
            }),
            stroke: new Stroke({
              color: FILL_COLOR,
              width: 3,
            }),
          }),
        }),
      );
    }
    if (typeof geometry.forEachSegment === "function") {
      geometry.forEachSegment(function (start, end) {
        const mid = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const rotation = Math.atan2(dy, dx);
        // arrows
        styles.push(
          new Style({
            geometry: new Point(mid),
            image: new Icon({
              src: "icon-arrow.png",
              anchor: [0.75, 0.5],
              rotateWithView: true,
              rotation: -rotation,
              color: LINE_COLOR,
              scale: [0.5, 0.5],
            }),
          }),
        );
      });
    }

    return styles;
  };

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    style: styleFunction,
  });

  // Generation counter so stale async parses (e.g. a previous Map It whose
  // boundary-code fetch is still in flight) discard themselves when a newer
  // `wkt` value arrives.
  let wktRunId = 0;

  wktInputControl.on("propertychange", async (event) => {
    // Only react to the keys we care about. Other propertychange events
    // (zoomToExtent / validWkt / errorMessage / anything the handler itself
    // sets) would otherwise re-enter this block with stale inputs.
    if (event.key === "wkt") {
      const input = event.target.get("wkt");
      if (input === undefined) return;
      const myRun = ++wktRunId;
      try {
        const idToken = azureLoginControl
          ? azureLoginControl.get("idToken")
          : null;

        // Parse input and fetch features (handles both WKT and boundary codes)
        const features = await parseInputToFeatures(input, idToken);

        // Bail if another `wkt` change started after us.
        if (myRun !== wktRunId) return;

        vectorSource.clear();
        vectorSource.addFeatures(features);

        // Only zoom to the WKT extent when the user explicitly clicked
        // "Map It". URL restoration sets "wkt" without the flag, so the
        // saved center/zoom in the URL is respected on reload.
        if (wktInputControl.get("zoomToExtent") === true) {
          wktInputControl.set("zoomToExtent", false);
          const extent = vectorLayer.getSource().getExtent();
          if (!isEmpty(extent)) {
            view.fit(buffer(extent, getHeight(extent) * 0.2), { maxZoom: 19 });
          }
        }

        wktInputControl.set("validWkt", true);
        wktInputControl.set("errorMessage", null);
      } catch (err) {
        if (myRun !== wktRunId) return;
        console.error(err);
        wktInputControl.set("validWkt", false);
        wktInputControl.set("errorMessage", err.message);
      }
      return;
    }

    if (event.key === "wktToggle") {
      vectorLayer.setVisible(event.target.get("wktToggle") === true);
    }
  });
  return vectorLayer;
}
